import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { usePortfolio, BACKEND_URL } from "../hooks/usePortfolio";
import { Reveal } from "../components/Reveal";
import { Typewriter } from "../components/Typewriter";

const entranceOffsets = [
  { x: -80, y: 70, rotate: -5 },
  { x: 0, y: 95, rotate: 2 },
  { x: 80, y: 70, rotate: 5 },
  { x: -70, y: -45, rotate: 4 },
  { x: 70, y: -45, rotate: -4 },
];

const workPath = (slug) => `/work/${encodeURIComponent(slug)}`;
const clientCategories = (client) => client.categories || [client.category].filter(Boolean);

export default function Work() {
  const { data: clients = [] } = usePortfolio();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(clients.flatMap(clientCategories)))],
    [clients]
  );
  const filteredClients =
    selectedCategory === "All"
      ? clients
      : clients.filter((c) => clientCategories(c).includes(selectedCategory));

  return (
    <main className="section page-top work-page" data-testid="work-page">
      <div className="work-intro">
        <p className="eyebrow">Cactus Branding</p>
        <h1 className="page-headline work-headline" data-testid="work-headline">
          <Typewriter lines={["Brand identities", "for satisfied clients."]} speed={42} startDelay={250} />
        </h1>
        <p className="work-subline">
          Logos, visual systems, and identity work built for brands that need to be recognized,
          remembered, and trusted.
        </p>
      </div>

      {categories.length > 1 && (
        <div className="work-filters" aria-label="Filter work by category">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              className={`work-filter ${selectedCategory === category ? "selected" : ""}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {filteredClients.length > 0 && (
        <div className="identity-grid work-logo-grid" data-testid="work-grid">
          {filteredClients.map((c, i) => {
            const offset = entranceOffsets[i % entranceOffsets.length];

            return (
              <motion.div
                key={`${selectedCategory}-${c.slug}`}
                className="identity-cell"
                initial={{ opacity: 0, scale: 0.86, ...offset }}
                whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{
                  type: "spring",
                  stiffness: 75,
                  damping: 16,
                  delay: i * 0.04,
                }}
              >
                <Link
                  to={workPath(c.slug)}
                  className="identity-box"
                  style={c.bg ? { background: c.bg } : undefined}
                  data-testid={`identity-box-${c.slug}`}
                >
                  <img className="identity-logo" src={`${BACKEND_URL}${c.logo}`} alt={c.name} loading="lazy" />
                  <span className="card-descriptor">{c.industry || c.name}</span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </main>
  );
}
